import { useId } from "react";

export default function LanguageIconMini({
  size = 28,
  active = false,
  title = "Select Language",
  ...rest
}) {
  const uid = useId();
  const id = (name) => `${uid}-${name}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label={title}
      style={{ display: "block" }}
      data-preserve
      {...rest}
    >
      <title>{title}</title>

      <defs>
        {/* Outer rim aura */}
        <radialGradient id={id("rimGlow")} cx="50%" cy="50%" r="50%">
          <stop offset="60%" stopColor="#00081a" stopOpacity="0" />
          <stop offset="90%" stopColor="#FF6DA8" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#5AA8FF" stopOpacity="0.6" />
        </radialGradient>

        {/* Rim stroke gradient */}
        <linearGradient
          id={id("rimStroke")}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor="#FF6DA8" />
          <stop offset="50%" stopColor="#a95cff" />
          <stop offset="100%" stopColor="#5AA8FF" />
        </linearGradient>

        {/* Inner face */}
        <radialGradient id={id("faceFill")} cx="50%" cy="45%" r="60%">
          <stop offset="0%" stopColor="#0d1020" />
          <stop offset="70%" stopColor="#05060d" />
          <stop offset="100%" stopColor="#000000" />
        </radialGradient>

        {/* Rim highlight */}
        <radialGradient id={id("rimHighlight")} cx="50%" cy="35%" r="70%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="60%" stopColor="#ffffff" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>

        {/* Neon gradient for globe lines */}
        <linearGradient
          id={id("iconStroke")}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor="#FF6DA8" />
          <stop offset="40%" stopColor="#ff4fa4" />
          <stop offset="60%" stopColor="#7b4dff" />
          <stop offset="100%" stopColor="#5AA8FF" />
        </linearGradient>

        {/* Glow filter */}
        <filter
          id={id("iconGlow")}
          x="-50%"
          y="-50%"
          width="200%"
          height="200%"
        >
          <feGaussianBlur
            in="SourceGraphic"
            stdDeviation="2.2"
            result="blur1"
          />
          <feColorMatrix
            in="blur1"
            type="matrix"
            values="
              1 0 0 0 0
              0 1 0 0 0
              0 0 1 0 0
              0 0 0 1 0
            "
          />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Active aura */}
        <filter
          id={id("activeAura")}
          x="-80%"
          y="-80%"
          width="260%"
          height="260%"
        >
          <feGaussianBlur stdDeviation="6" result="b1" />
          <feMerge>
            <feMergeNode in="b1" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Sparkle blur */}
        <filter id={id("spark")} x="-200%" y="-200%" width="400%" height="400%">
          <feGaussianBlur stdDeviation="0.6" />
        </filter>
      </defs>

      {/* === COIN BODY === */}
      <g filter={active ? `url(#${id("activeAura")})` : undefined}>
        <circle cx="32" cy="32" r="30" fill={`url(#${id("rimGlow")})`} />
        <circle
          cx="32"
          cy="32"
          r="30"
          fill="none"
          stroke={`url(#${id("rimStroke")})`}
          strokeWidth="4"
        />
        <circle
          cx="32"
          cy="24"
          r="26"
          fill={`url(#${id("rimHighlight")})`}
          opacity="0.4"
        />
        <circle cx="32" cy="32" r="24" fill={`url(#${id("faceFill")})`} />
        <circle
          cx="32"
          cy="32"
          r="24"
          fill="none"
          stroke="#ffffff"
          strokeOpacity="0.18"
          strokeWidth="1.25"
        />
      </g>

      {/* === GLOBE ICON === */}
      <g
        filter={`url(#${id("iconGlow")})`}
        stroke={`url(#${id("iconStroke")})`}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        {/* Outer circle of the globe */}
        <circle cx="32" cy="32" r="8" />

        {/* Vertical longitude line */}
        <path d="M32 24 A8 8 0 0 1 32 40" />

        {/* Left and right curved meridians */}
        <path d="M28 24 A12 8 0 0 0 28 40" />
        <path d="M36 24 A12 8 0 0 1 36 40" />

        {/* Two horizontal latitude lines */}
        <path d="M24 28 H40" />
        <path d="M24 36 H40" />
      </g>

      {/* Sparkles on rim */}
      <g opacity="0.9">
        <circle
          cx="20"
          cy="22"
          r="0.8"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
        <circle
          cx="44"
          cy="18"
          r="0.6"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
        <circle
          cx="48"
          cy="36"
          r="0.7"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
        <circle
          cx="18"
          cy="40"
          r="0.5"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
      </g>
    </svg>
  );
}
